import { useQueryClient } from '@tanstack/react-query';
import { createContext, FC, PropsWithChildren, useCallback, useEffect, useMemo, useRef } from 'react';
import { Filter, GroupLevel } from 'src/domain';
import { useDevTools } from 'src/hooks/devtools/useDevTools';
import { useStoreType } from 'src/hooks/useStoreType';
import { useType } from 'src/hooks/useType';
import { StoreModel, StoreType } from 'src/infrastructure/rest-api/api-types';
import { useApiQuery } from 'src/infrastructure/rest-api/useApi';
import { GroupedStoresResult, groupStores } from 'src/mapping/store.mapping';
import { SerializeMap } from 'src/utils/SerializeMap';

interface StoreCacheKey {
    groupBy: GroupLevel | null;
    storeType: StoreType;
    filter?: Filter;
}

interface StoreCacheContextState {
    loading: boolean;
    error: Error | null;
    refetch: () => void;
}

type GetStoresReturnValue = {
    loading: boolean;
    allStores: StoreModel[] | undefined;
    groupedStores: GroupedStoresResult;
};

type StoreCacheContextValue = readonly [
    (groupBy?: GroupLevel | null, storeType?: StoreType, filter?: Filter) => GetStoresReturnValue,
    StoreCacheContextState,
];

export const StoreCacheContext = createContext<StoreCacheContextValue>([
    () => ({}) as GetStoresReturnValue,
    // biome-ignore lint/suspicious/noEmptyBlockStatements: placeholder
    { loading: true, error: null, refetch: () => {} },
]);

export const StoreCacheContextProvider: FC<PropsWithChildren<unknown>> = ({ children }) => {
    const queryClient = useQueryClient();
    const { data: stores, loading, error, refetch } = useApiQuery('/api/stores', 'get');
    const { impersonateEmail, isImpersonating } = useDevTools();

    const defaultGroupBy = useType();
    const defaultStoreType = useStoreType();
    const groupedResultsCache = useRef(new SerializeMap<StoreCacheKey | string, GroupedStoresResult>());

    const doGrouping = useCallback(
        (stores: StoreModel[] | undefined, groupBy: GroupLevel | null, storeType: StoreType, filter?: Filter) => {
            if (!stores) {
                return {
                    groupedStores: undefined,
                    storeCount: 0,
                    chains: [],
                    clusters: [],
                    countries: [],
                    partners: [],
                    allStores: [],
                    allStoresLookup: new Map(),
                };
            }

            const cacheKey = { groupBy, storeType, filter };
            const cachedResult = groupedResultsCache.current.get(cacheKey);

            if (cachedResult) {
                return cachedResult;
            }

            const result = groupStores(
                stores.map((store) => ({
                    ...store,
                    storeName: store.storeName ?? null,
                })),
                groupBy,
                storeType,
                filter
            );

            groupedResultsCache.current.set(cacheKey, result);

            const keys = Array.from(groupedResultsCache.current.keys());
            if (keys.length > 10) {
                const firstKey = keys[0];
                if (firstKey) {
                    groupedResultsCache.current.delete(firstKey);
                }
            }

            return result;
        },
        []
    );

    const normalizeStores = useCallback((stores: StoreModel[] | undefined): StoreModel[] | undefined => {
        return stores?.map((store) => ({
            ...store,
            storeName: store.storeName?.trimChain() ?? null,
        }));
    }, []);

    const normalizedStores = useMemo(() => normalizeStores(stores), [normalizeStores, stores]);

    const refetchStores = useCallback(() => {
        groupedResultsCache.current.clear();
        return refetch().then((res) => {
            doGrouping(normalizeStores(res.data), defaultGroupBy, defaultStoreType);
        });
    }, [defaultGroupBy, defaultStoreType, doGrouping, refetch, normalizeStores]);

    const getStores = useCallback(
        (groupBy?: GroupLevel | null, storeType?: StoreType, filter?: Filter) => {
            const groupByToUse = groupBy ?? defaultGroupBy;
            const storeTypeToUse = storeType ?? defaultStoreType;
            return {
                loading,
                allStores: normalizedStores,
                groupedStores: doGrouping(normalizedStores, groupByToUse, storeTypeToUse, filter),
            };
        },
        [defaultGroupBy, defaultStoreType, doGrouping, loading, normalizedStores]
    );

    const isImpersonatingRef = useRef<boolean>(false);
    useEffect(() => {
        if (isImpersonating !== isImpersonatingRef.current && impersonateEmail) {
            queryClient.cancelQueries({ queryKey: ['/api/stores', undefined] }).then(() => {
                refetchStores();
            });
            isImpersonatingRef.current = isImpersonating;
        }
    }, [impersonateEmail, isImpersonating, queryClient, refetchStores]);

    const contextValue = useMemo(() => {
        return [getStores, { loading, error, refetch: refetchStores }] as const;
    }, [error, getStores, loading, refetchStores]);

    return <StoreCacheContext.Provider value={contextValue}>{children}</StoreCacheContext.Provider>;
};
